import React, { useRef, useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, Platform, TouchableOpacity, ActivityIndicator, Animated, Easing } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { Audio } from 'expo-av';
import { Message, Tutor } from '../types';
import * as ttsService from '../services/ttsService';
import { useSettings } from '../contexts/SettingsContext';
import { useAudioState } from '../contexts/AudioContext';
import { useChatContext } from '../contexts/ChatContext';
import { useTutor } from '../contexts/TutorContext';
import Avatar from './Avatar';
import { translateText } from '../api/translateApi';
import { simplifyText } from '../api/services/simplifyApi';
import { getTranslation } from '../i18n/translations';
import CustomSlider from './CustomSlider';

interface AIMessageProps {
  message: Message;
  tutor: Tutor;
  isFromNewResponse?: boolean;
  onRetry?: () => void;
  onStop?: () => void;
  error?: string;
  onComplexityChange?: (type: 'increase' | 'decrease') => void;
  onTranslate?: () => void;
}

interface AudioControlButtonProps {
  text: string;
  isPlaying: boolean;
  timestamp: number;
  onPress: () => void;
  tutor: Tutor;
}

export const AIMessage: React.FC<AIMessageProps> = ({
  message,
  tutor,
  isFromNewResponse = false,
  onRetry,
  onStop,
  error,
  onComplexityChange,
  onTranslate,
}) => {
  const { currentlyPlayingId, setCurrentlyPlayingId } = useAudioState();
  const [isPlaying, setIsPlaying] = useState(false);
  const [errorState, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [translatedText, setTranslatedText] = useState<string | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [simplifiedText, setSimplifiedText] = useState<string | null>(null);
  const [isSimplifying, setIsSimplifying] = useState(false);
  const [simplificationLevel, setSimplificationLevel] = useState(0);
  const [tempSliderValue, setTempSliderValue] = useState(0);
  const [originalMessageText] = useState(message.text);
  const [isSliderActive, setIsSliderActive] = useState(false);
  const soundRef = useRef<Audio.Sound | null>(null);
  const isMountedRef = useRef(true);
  const { nativeLanguage, userInfo, interfaceLanguage } = useSettings();
  const { selectedTutor } = useTutor();
  const { currentConversationId } = useChatContext();
  const [isNewMessage, setIsNewMessage] = useState(isFromNewResponse);
  const hasPlayedRef = useRef(false);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Auto-play new messages if enabled
  useEffect(() => {
    const autoPlayNewMessage = async () => {
      if (
        isFromNewResponse && 
        !hasPlayedRef.current && 
        userInfo.autoPlayAudio && 
        isMountedRef.current
      ) {
        console.log('Auto-playing new AI response:', message);
        hasPlayedRef.current = true;
        await handleTTSPlay();
      }
    };

    autoPlayNewMessage();
  }, [isFromNewResponse, message, userInfo.autoPlayAudio]);

  // Reset states when message changes
  useEffect(() => {
    setIsNewMessage(isFromNewResponse);
    hasPlayedRef.current = false;
    setIsPlaying(false);
    setIsPaused(false);
    setError(null);
    setSimplifiedText(null);
    setSimplificationLevel(0);
    cleanupSound();
  }, [message.id, isFromNewResponse, cleanupSound]);

  // Cleanup function to unload sound
  const cleanupSound = useCallback(async () => {
    if (!isMountedRef.current) return;

    try {
      const currentSound = soundRef.current;
      if (currentSound) {
        // Clear the reference first
        soundRef.current = null;
        
        // Remove the listener
        currentSound.setOnPlaybackStatusUpdate(null);

        try {
          const status = await currentSound.getStatusAsync();
          if (status.isLoaded) {
            if (status.isPlaying) {
              await currentSound.stopAsync();
            }
            await currentSound.unloadAsync();
          }
        } catch (err) {
          // Ignore errors during cleanup
          console.log('Cleanup warning:', err);
        }

        // Update state if component is still mounted
        if (isMountedRef.current) {
          setIsPlaying(false);
          setCurrentlyPlayingId(null);
          setError(null);
        }
      }
    } catch (err) {
      console.log('General cleanup warning:', err);
    }
  }, []);

  useEffect(() => {
    // Reset playing state when currentlyPlayingId changes
    if (currentlyPlayingId !== message.id) {
      setIsPlaying(false);
    }
  }, [currentlyPlayingId, message.id]);

  const handlePlaybackStatusUpdate = useCallback(async (status: any) => {
    if (!isMountedRef.current) return;

    try {
      if (status.isLoaded) {
        // Handle playback completion
        if (!status.isPlaying && status.didJustFinish) {
          console.log('Playback finished');
          setIsPlaying(false);
          setCurrentlyPlayingId(null);
          await cleanupSound();
        }
        // Handle error states
        else if (status.error) {
          console.error('Playback error:', status.error);
          setError('Playback error occurred');
          setIsPlaying(false);
          setCurrentlyPlayingId(null);
          await cleanupSound();
        }
      }
    } catch (err) {
      console.error('Playback status update error:', err);
      setIsPlaying(false);
      setCurrentlyPlayingId(null);
    }
  }, [setCurrentlyPlayingId, cleanupSound]);

  const handleTTSPlay = useCallback(async () => {
    if (!isMountedRef.current) return;

    try {
      // If currently playing, stop it
      if (isPlaying && !isPaused) {
        await ttsService.stopTTS();
        setIsPlaying(false);
        setIsPaused(false);
        setCurrentlyPlayingId(null);
        return;
      }

      // If paused, resume
      if (isPaused) {
        await ttsService.resumeTTS();
        setIsPaused(false);
        setIsPlaying(true);
        return;
      }

      setIsLoading(true);
      setError(null);

      // Clean up any existing sound
      await cleanupSound();

      // Stop any currently playing audio
      if (currentlyPlayingId && currentlyPlayingId !== message.id) {
        setCurrentlyPlayingId(null);
      }

      // Set playing state before starting playback
      setIsPlaying(true);
      setIsPaused(false);
      setCurrentlyPlayingId(message.id);

      const language = selectedTutor.language_code;
      const options = {
        voice: selectedTutor.voice_id,
        speed: parseFloat(userInfo.speechSpeed || '1.0'),
        audioFormat: 'OGG_OPUS',
        pitch: 0.0
      };

      // Get the current text to play (simplified or original)
      const textToPlay = simplifiedText || message.text;

      // Start synthesis and playback
      await ttsService.synthesizeRealTimeSpeech(
        textToPlay,
        '',  // token will be fetched by the service
        language,
        options,
        () => {
          if (isMountedRef.current) {
            console.log('[AIMessage] TTS playback completed');
            setIsPlaying(false);
            setIsPaused(false);
            setCurrentlyPlayingId(null);
            cleanupSound();
          }
        }
      );

    } catch (error) {
      console.error('Error playing TTS:', error);
      setError('Failed to play audio');
      setIsPlaying(false);
      setIsPaused(false);
      setCurrentlyPlayingId(null);
    } finally {
      setIsLoading(false);
    }
  }, [
    isPlaying,
    isPaused,
    message.id,
    message.text,
    simplifiedText,
    currentlyPlayingId,
    cleanupSound,
    selectedTutor.language_code,
    selectedTutor.voice_id,
    userInfo.speechSpeed,
  ]);

  const handleTranslate = async () => {
    try {
      setIsTranslating(true);
      // Use the currently displayed text (simplified or original)
      const textToTranslate = simplifiedText || message.text;
      const translated = await translateText(textToTranslate, userInfo.nativeLanguage.toLowerCase());
      // Clean up the translation text by removing the prefix if it exists
      const cleanTranslation = translated.replace(/Here is the translation of the text to [^:]+:\s*"?(.+?)"?$/, '$1');
      setTranslatedText(cleanTranslation);
    } catch (error) {
      console.error('Translation failed:', error);
      setError('Translation failed');
    } finally {
      setIsTranslating(false);
    }
  };

  const handleSliderChange = (value: number) => {
    // Update temporary value during sliding
    const position = Math.round(value);
    setTempSliderValue(position);
  };

  const handleSliderStart = () => {
    setIsSliderActive(true);
  };

  const handleSliderComplete = async (value: number) => {
    setIsSliderActive(false);
    // Round to nearest position (0, 1, or 2)
    const position = Math.round(value);
    let simplificationValue;
    
    switch (position) {
      case 0:
        simplificationValue = 0;
        break;
      case 1:
        simplificationValue = 20;
        break;
      case 2:
        simplificationValue = 50;
        break;
      default:
        simplificationValue = 0;
    }
    
    setSimplificationLevel(position);
    // Clear any existing translation when changing simplification level
    setTranslatedText(null);
    
    // If returning to original text, ensure we clear simplified text
    if (position === 0) {
      setSimplifiedText(null);
      setIsSimplifying(false);
      return;
    }
    
    try {
      setIsSimplifying(true);
      const baseLanguage = selectedTutor.language_code.split('-')[0];
      const simplified = await simplifyText(
        originalMessageText,
        baseLanguage,
        simplificationValue,
        userInfo,
        currentConversationId
      );
      setSimplifiedText(simplified);
    } catch (error) {
      console.error('Simplification failed:', error);
      setError('Simplification failed');
    } finally {
      setIsSimplifying(false);
    }
  };

  const getSimplificationLabel = (position: number) => {
    switch (position) {
      case 0:
        return getTranslation('originalAnswer', interfaceLanguage);
      case 1:
        return getTranslation('simplification1x', interfaceLanguage);
      case 2:
        return getTranslation('simplification2x', interfaceLanguage);
      default:
        return '';
    }
  };

  const handleSlidingComplete = () => {};
  const handleSlidingStart = () => {};

  const LoadingDots = () => {
    const [dots, setDots] = useState('');
    
    useEffect(() => {
      const interval = setInterval(() => {
        setDots(prev => {
          if (prev === '...') return '';
          return prev + '.';
        });
      }, 500);
      
      return () => clearInterval(interval);
    }, []);
    
    return (
      <Text style={styles.loadingDots}>{dots}</Text>
    );
  };

  // Cleanup effect
  useEffect(() => {
    return () => {
      const cleanup = async () => {
        await cleanupSound();
        isMountedRef.current = false;
      };
      cleanup();
    };
  }, [cleanupSound]);

  return (
    <View style={styles.container}>
      <View style={styles.messageWrapper}>
        <Avatar size={80} avatarUrl={selectedTutor?.avatar_url} style={{ alignSelf: 'flex-start' }} />
        <View style={styles.messageContainer}>
          <View style={styles.contentContainer}>
            <View style={styles.textContainer}>
              <Text style={styles.messageText} numberOfLines={0}>
                {simplifiedText === null ? message.text : simplifiedText}
              </Text>
              {isTranslating && (
                <Text style={[styles.translatedText, styles.loadingText]}>Translating...</Text>
              )}
              {translatedText && (
                <Text style={styles.translatedText}>{translatedText}</Text>
              )}
            </View>
            <View style={styles.buttonsContainer}>
              {/* <View style={styles.sliderContainer}>
                <View style={styles.sliderWrapper}>
                  <View style={{ pointerEvents: isLoading ? 'none' : 'auto' }}>
                    <CustomSlider
                      style={styles.slider}
                      value={isSliderActive ? tempSliderValue : simplificationLevel}
                      minimumValue={0}
                      maximumValue={2}
                      step={1}
                      onValueChange={handleSliderChange}
                      onSlidingStart={handleSliderStart}
                      onSlidingComplete={handleSliderComplete}
                      minimumTrackTintColor="rgba(75, 127, 219, 0.8)"
                      maximumTrackTintColor="rgba(75, 127, 219, 0.2)"
                      thumbTintColor="#4B7FDB"
                      disabled={isLoading || isSimplifying}
                    />
                  </View>
                  <View style={styles.labelContainer}>
                    <Text style={styles.simplificationLabel}>
                      {getSimplificationLabel(isSliderActive ? tempSliderValue : simplificationLevel)}
                    </Text>
                    {isSimplifying && <LoadingDots />}
                  </View>
                </View>
              </View> */}
              <TouchableOpacity
                style={[styles.button, styles.buttonMargin]}
                onPress={handleTranslate}
                disabled={isTranslating}
              >
                <MaterialIcons name="translate" size={18} color="#4B7FDB" />
              </TouchableOpacity>
              {/* Temporarily disabled play button
              <AudioControlButton
                text={message.text}
                isPlaying={isPlaying && currentlyPlayingId === message.id}
                timestamp={message.timestamp}
                onPress={handleTTSPlay}
                tutor={selectedTutor}
              /> */}
            </View>
          </View>
        </View>
      </View>
    </View>
  );
};

/* Temporarily disabled AudioControlButton
const AudioControlButton: React.FC<AudioControlButtonProps> = ({
  text,
  isPlaying,
  timestamp,
  onPress,
  tutor,
}) => {
  const pulseAnim = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    let animation: Animated.CompositeAnimation;
    
    if (isPlaying) {
      pulseAnim.setValue(1);
      animation = Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, {
            toValue: 1.4,
            duration: 2000,
            useNativeDriver: Platform.OS === 'web' ? false : true,
            easing: Easing.bezier(0.25, 0.1, 0.25, 1),
          }),
          Animated.timing(pulseAnim, {
            toValue: 1,
            duration: 2000,
            useNativeDriver: Platform.OS === 'web' ? false : true,
            easing: Easing.bezier(0.25, 0.1, 0.25, 1),
          }),
        ])
      );
      animation.start();
    } else {
      pulseAnim.setValue(1);
    }

    return () => {
      if (animation) {
        animation.stop();
      }
    };
  }, [isPlaying]);

  const opacity = pulseAnim.interpolate({
    inputRange: [1, 1.2, 1.4],
    outputRange: [0.4, 0.2, 0],
    extrapolate: 'clamp'
  });

  return (
    <View style={styles.playButtonContainer}>
      {isPlaying && (
        <Animated.View
          style={[
            styles.pulseCircle,
            {
              transform: [{ scale: pulseAnim }],
              opacity,
            },
          ]}
        />
      )}
      <TouchableOpacity
        style={[styles.playButton, styles.buttonMargin, isPlaying && styles.playButtonPressed]}
        onPress={onPress}
      >
        <MaterialIcons
          name={isPlaying ? 'pause' : 'play-arrow'}
          size={18}
          color="#4B7FDB"
        />
      </TouchableOpacity>
    </View>
  );
};
*/

const styles = StyleSheet.create({
  container: {
    alignSelf: 'flex-start',
    maxWidth: '100%',
    marginRight: Platform.OS === 'web' ? 16 : 8,
    paddingLeft: 0,
  },
  messageWrapper: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    marginLeft: 5,
    marginVertical: 4,
    maxWidth: '100%',
  },
  messageContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 4,
    shadowColor: '#2D6BDB',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.06,
    shadowRadius: 4,
    elevation: 2,
    borderWidth: 1,
    borderColor: '#E6F0FF',
    maxWidth: '85%',
    flexShrink: 1,
  },
  contentContainer: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 4,
    padding: 8,
  },
  textContainer: {
    maxWidth: '100%',
  },
  messageText: {
    color: '#4B7FDB',
    fontSize: 15,
    lineHeight: 22,
  },
  translatedText: {
    fontStyle: 'italic',
    fontSize: Math.round(15 * 0.9),
    color: '#666666',
    marginTop: 8,
    marginBottom: 4,
  },
  loadingText: {
    color: '#999999',
  },
  buttonsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  button: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: '#F5F8FF',
    justifyContent: 'center',
    alignItems: 'center',
  },
  buttonMargin: {
    marginLeft: 8,
  },
  playButton: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: '#F5F8FF',
    justifyContent: 'center',
    alignItems: 'center',
    transform: [{ scale: 1 }],
  },
  playButtonPressed: {
    backgroundColor: '#F5F8FF',
    transform: [{ scale: 0.95 }],
  },
  playButtonContainer: {
    position: 'relative',
    marginLeft: 8,
    width: 30,
    height: 30,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pulseCircle: {
    position: 'absolute',
    width: 46,
    height: 46,
    borderRadius: 23,
    backgroundColor: '#E0E0E0',
    left: 0,
    top: 0,
    zIndex: -1,
  },
  sliderContainer: {
    marginTop: 8,
    marginBottom: 4,
    width: '100%',
    maxWidth: 140,
    alignSelf: 'flex-end',
  },
  sliderWrapper: {
    width: '100%',
  },
  slider: {
    width: '100%',
    height: 24,
    ...Platform.select({
      ios: {},
      android: {
        paddingHorizontal: 0,
      },
    }),
  },
  labelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginTop: 2,
    gap: 4,
    minHeight: 15,
  },
  simplificationLabel: {
    fontSize: 11,
    color: '#6B7280',
    fontWeight: '500',
  },
  loadingDots: {
    fontSize: 11,
    color: '#6B7280',
    fontWeight: '500',
    width: 15,
    textAlign: 'left',
  },
});

export default AIMessage;
